"use client";

import React from "react";

import { ArticleJsonLd, WebPageJsonLd } from "next-seo";

import { useSeoMeta } from "@/hooks/useSeoMeta";

import { SeoHeadClientProps, SeoMetadata } from "./SeoHead.types";

/**
 * Клиентская версия компонента для управления дополнительными SEO-метаданными
 * Этот компонент в основном фокусируется на структурированных данных JSON-LD,
 * так как основные метатеги уже обрабатываются на сервере через Next.js Metadata API
 */
const SeoHeadClient: React.FC<SeoHeadClientProps> = ({
  url,
  pageType,
  webPageProps,
  articleProps,
  metadata,
}) => {
  // Если метаданные переданы напрямую (с сервера), используем их
  // Иначе, используем хук useSeoMeta для загрузки данных на клиенте
  const { data, isLoading, error } = useSeoMeta(url);

  // Приоритет отдаем переданным метаданным с сервера
  const seoData: SeoMetadata | null = metadata || data;

  // Если данные еще загружаются, произошла ошибка, или данных нет, не рендерим
  if ((isLoading && !metadata) || error || !seoData) {
    return null;
  }

  const { title, description, canonical } = seoData;

  // Рендерим только JSON-LD разметку, так как основные SEO теги
  // уже обработаны на сервере через Next.js Metadata API
  return (
    <>
      {pageType === "webpage" && (
        <WebPageJsonLd
          id={url || window.location.pathname}
          title={title}
          description={description}
          url={canonical || url || ""}
          datePublished={webPageProps?.datePublished}
          dateModified={webPageProps?.dateModified}
          authorName={webPageProps?.authorName}
        />
      )}

      {pageType === "article" && articleProps && (
        <ArticleJsonLd
          url={canonical || url || ""}
          title={title}
          description={description}
          datePublished={articleProps.datePublished}
          dateModified={articleProps.dateModified || articleProps.datePublished}
          authorName={articleProps.authorName}
          publisherName={articleProps.publisherName || "Avezor"}
          publisherLogo={
            articleProps.publisherLogo || "https://avezor.com/images/logo.png"
          }
          images={articleProps.images || []}
          isAccessibleForFree={true}
        />
      )}
    </>
  );
};

export default SeoHeadClient;
